import type { SupplierDirectoryListResponse } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import type { MouseEvent } from 'react'

import { captureException } from '@/domains/observability'

import { favoriteSupplier, unfavoriteSupplier } from './api'

type Props = {
  slug: string
  supplierId: string
  isFavorited: boolean
}

/** star toggle that lives inside the supplier-directory list table row.
 * stops click propagation so it doesn't navigate to the detail page, and
 * uses optimistic update on the list cache so the fill flips instantly.
 * onError reverts. invalidates the list query in onSettled so any stale
 * derived data converges with the server.
 */
export function FavoriteStar({ slug, supplierId, isFavorited }: Props) {
  const queryClient = useQueryClient()
  /** prefix filter: the list is server-paginated, so there's one cache entry
   * per page/filter combo under this key. flip the star across all of them.
   */
  const listKey = ['supplier-directory', 'list']

  const mutation = useMutation({
    mutationFn: async (next: boolean) =>
      next ? favoriteSupplier(slug) : unfavoriteSupplier(slug),
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      queryClient.setQueriesData<SupplierDirectoryListResponse>(
        { queryKey: listKey },
        previous =>
          previous
            ? {
                ...previous,
                items: previous.items.map(item =>
                  item.supplierId === supplierId
                    ? { ...item, isFavorited: next }
                    : item
                )
              }
            : previous
      )
    },
    onError: error => {
      void queryClient.invalidateQueries({ queryKey: listKey })
      captureException(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey })
    }
  })

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    mutation.mutate(!isFavorited)
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      aria-label={isFavorited ? 'Unfavorite supplier' : 'Favorite supplier'}
      aria-pressed={isFavorited}
      className='grid size-11 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-amber-500 sm:size-7'
    >
      <Star
        className={`size-4 transition-colors ${isFavorited ? 'fill-amber-400 text-amber-500' : ''}`}
      />
    </button>
  )
}
